import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import ProtectedRoute from '../components/ProtectedRoute/ProtectedRoute';
import PublicRoute from '../components/PublicRoute/PublicRoute';
import AuthInitializer from '../components/AuthInitializer/AuthInitializer';
import Layout from '../components/Layout';
import PageLoading from '../components/PageLoading/PageLoading';

// 懒加载页面组件
const LoginPage = lazy(() => import('../pages/Login/LoginPage'));
const OAuthCallbackPage = lazy(() => import('../pages/OAuthCallback/OAuthCallbackPage'));
const JoinOrganizationPage = lazy(() => import('../pages/JoinOrganization/JoinOrganizationPage'));
const HomePage = lazy(() => import('../pages/Home/HomePage'));
const APIKeysPage = lazy(() => import('../pages/APIKeys/APIKeysPage'));
const GuardrailsPage = lazy(() => import('../pages/Guardrails/GuardrailsPage'));
const LogsPage = lazy(() => import('../pages/Logs/LogsPage'));
const ChangePasswordPage = lazy(() => import('../pages/ChangePassword/ChangePasswordPage'));
const MembersPage = lazy(() => import('../pages/Members/MembersPage'));
const ProjectsPage = lazy(() => import('../pages/Projects/ProjectsPage'));
const ReportPage = lazy(() => import('../pages/Report/ReportPage'));
const TopupPage = lazy(() => import('../pages/Topup/TopupPage'));

/**
 * User portal router configuration
 * - /login: Login page (public access, auto-redirect for logged-in users)
 * - /home: Home page (requires authentication)
 * - /: Root path redirects to /home
 */
export const router = createBrowserRouter([
  // Public routes - logged-in users will be auto-redirected
  {
    element: <PublicRoute />,
    children: [
      {
        path: '/login',
        element: (
          <Suspense fallback={<PageLoading />}>
            <LoginPage />
          </Suspense>
        ),
      },
    ],
  },

  // OAuth 回调路由（不需要认证保护，也不需要 PublicRoute 的自动重定向）
  {
    path: '/oauth/callback',
    element: (
      <Suspense fallback={<PageLoading />}>
        <OAuthCallbackPage />
      </Suspense>
    ),
  },

  // Protected routes - requires authentication, uses Layout
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/join-organization',
        element: (
          <Suspense fallback={<PageLoading />}>
            <JoinOrganizationPage />
          </Suspense>
        ),
      },
      {
        element: (
          <AuthInitializer>
            <Layout>
              <Suspense fallback={<PageLoading />}>
                <Outlet />
              </Suspense>
            </Layout>
          </AuthInitializer>
        ),
        children: [
          {
            path: '/home',
            element: <HomePage />,
          },
          {
            path: '/report',
            element: <ReportPage />,
          },
          {
            path: '/topup',
            element: <TopupPage />,
          },
          {
            path: '/keys',
            element: <APIKeysPage />,
          },
          {
            path: '/guardrails',
            element: <GuardrailsPage />,
          },
          {
            path: '/logs',
            element: <LogsPage />,
          },
          {
            path: '/change-password',
            element: <ChangePasswordPage />,
          },
          {
            path: '/members',
            element: <MembersPage />,
          },
          {
            path: '/projects',
            element: <ProjectsPage />,
          },
          // Add more protected routes here
          // {
          //   path: '/organizations/list',
          //   element: <OrganizationListPage />,
          // },
          // {
          //   path: '/projects/list',
          //   element: <ProjectListPage />,
          // },
          // {
          //   path: '/change-password',
          //   element: <ChangePasswordPage />,
          // },
        ],
      },
    ],
  },

  // Root path redirect
  {
    path: '/',
    element: <Navigate to="/home" replace />,
  },

  // 404 page - unmatched routes redirect to home
  {
    path: '*',
    element: <Navigate to="/home" replace />,
  },
]);

export default router;
